// Floating Letters Configuration
export const floatingLetters = ['W', 'O', 'R', 'D', 'H', 'E', 'I', 'S', 'T', 'B', 'C', 'I', 'J', 'K', 'L', 'F', 'U', 'N', 'G', 'A', 'Q', 'P'];

// Shared CSS for background animations and floating letters
export const sharedAnimationCSS = `
  @keyframes retroFlow {
    0%, 100% { background-position: 0% 50%; }
    25% { background-position: 100% 50%; }
    50% { background-position: 50% 100%; }
    75% { background-position: 0% 0%; }
  }

  @keyframes floatLetter {
    0% {
      transform: translateY(0) rotate(0deg);
      opacity: 0;
    }
    5% {
      opacity: 0.2;
    }
    95% {
      opacity: 0.2;
    }
    100% {
      transform: translateY(-100vh) rotate(360deg);
      opacity: 0;
    }
  }

  .floating-letter {
    position: fixed;
    pointer-events: none;
    z-index: 1;
    // font-family: 'Bungee', cursive;
    font-weight: bold;
    font-size: 0.9rem;
    // color: rgba(139, 69, 19, 0.3);
    color:rgba(164, 129, 129, 0.77);
    bottom: -50px;
    opacity: 0;
    animation: floatLetter 12s linear infinite;
    text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.1);
  }

  .floating-letter.delayed-1 { animation-delay: 1s; }
  .floating-letter.delayed-2 { animation-delay: 2s; }
  .floating-letter.delayed-3 { animation-delay: 3s; }
  .floating-letter.delayed-4 { animation-delay: 4s; }
  .floating-letter.delayed-5 { animation-delay: 5s; }
  .floating-letter.delayed-6 { animation-delay: 6s; }
  .floating-letter.delayed-7 { animation-delay: 7s; }
  .floating-letter.delayed-8 { animation-delay: 8s; }

  body {
    background-size: 400% 400%;
    animation: retroFlow 15s ease-in-out infinite;
    background-attachment: fixed;
  }
`;

// Extract just floating letter styles (no body styles) from sharedAnimationCSS
export const floatingLettersOnlyCSS = sharedAnimationCSS.replace(/\s*body\s*\{[^}]*\}/g, '');

// For backward compatibility
export const floatingLetterCSS = sharedAnimationCSS;

// Shared styled components (need to be created in each component file due to styled-components scoping)
export const createSharedComponents = (styled, createGlobalStyle) => {
  const SharedGlobalStyle = createGlobalStyle`
    ${sharedAnimationCSS}
    
    body {
      background: ${props => props.theme?.bodyGradient || 'transparent'};
      color: ${props => props.theme?.text || 'inherit'};
      font-family: ${props => props.theme?.fontFamily || 'inherit'};
    }
  `;

  const SharedBackgroundContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    min-height: 100vh;
    padding: 2rem 0;
    background: ${props => props.theme.bodyGradient};
    background-size: 400% 400%;
    animation: retroFlow 15s ease-in-out infinite;
    background-attachment: fixed;
    color: ${props => props.theme.text};
    font-family: ${props => props.theme.fontFamily};
    overflow-y: auto;
    position: relative;
  `;

  return { SharedGlobalStyle, SharedBackgroundContainer };
};

// Shared floating letters utility
export const createFloatingLetterEffect = (intervalMs = 3000, initialCount = 8, initialDelay = 1500) => {
  const createFloatingLetter = () => {
    const letter = document.createElement('div');
    letter.className = `floating-letter`;
    letter.textContent = floatingLetters[Math.floor(Math.random() * floatingLetters.length)];
    letter.style.left = Math.random() * 90 + 5 + '%'; // Keep letters away from edges
    letter.style.animationDuration = (Math.random() * 4 + 8) + 's';
    // fontSize is controlled by CSS in theme
    
    document.body.appendChild(letter);
    
    // Remove letter after animation completes
    const duration = parseFloat(letter.style.animationDuration) * 1000;
    setTimeout(() => {
      if (letter.parentNode) {
        letter.parentNode.removeChild(letter);
      }
    }, duration);
  };

  // Create initial letters
  for (let i = 0; i < initialCount; i++) {
    setTimeout(createFloatingLetter, i * initialDelay);
  }

  // Periodically create new letters
  const interval = setInterval(createFloatingLetter, intervalMs);

  // Return cleanup function
  return () => clearInterval(interval);
};

// 70s Kitchen Theme (Light)
export const lightTheme = {
  fontFamily: "'Bungee', cursive",
  instructionFont: "'Nunito', sans-serif",
  foundWordsFont: "'Courier New', 'Monaco', 'Menlo', monospace",
  body: '#F5EFE6', // Almond off-white (fallback)
  // bodyGradient: 'linear-gradient(135deg, #F5EFE6 0%, #E57A44 20%, #FFC107 40%, #8A9A5B 60%, #D3C5B3 80%, #F5EFE6 100%)',
  // bodyGradient: 'linear-gradient(135deg, #5C4B51 0%, #8CBEB2 20%, #F2EBBF 40%, #F3B562 60%, #F06060 80%)',
  bodyGradient: 'linear-gradient(135deg, #a1a8be 0%, #edbee4 20%, #cbdfd8 40%, #dbddb6 60%, #f3d0c3 80%, #F3dfde 100%)',
  text: '#8B4513', // Saddle Brown
  textShadow: '2px 2px 3px rgba(255, 255, 255, 0.7)', // Lighter shadow for dark text
  tileDefault: '#D3C5B3', // Muted beige
  tileDefaultBorder: '#BEAE9D',
  tilePlayer: '#E57A44', // Burnt sienna (main player color)
  tilePlayerBorder: '#C35A22',
  tileAI: '#8A9A5B', // Back to avocado green
  tileAIBorder: '#6B3410', // Deeper brown for better contrast with player border
  tileFound: '#FFC107',  // Harvest gold (User preference)
  tileFoundBorder: '#68783B', // Kept original for consistency with the tile
  tileText: 'black',
  tileOpponent: '#8A9A5B', // Back to avocado green
  tileOpponentBorder: '#6B3410', // Deeper brown for better contrast with player border
  tileSelectedBorder: '#8774de', // Purple for swap selection
  controlsBackground: '#EAE1D6',
  controlsBorder: '#8B4513',
  controlsText: '#8B4513',
  buttonColor: '#8A9A5B', // Dedicated color for buttons, using original avocado green
  // Opponent colors for multiplayer (2-5 players)
  opponentColors: [
    '#3498DB', // Bright blue
    '#9B59B6', // Purple
    '#E74C3C', // Red
    '#F39C12', // Amber/Gold
  ],
};

// tileAI: '#4B8E8D', // Muted teal
// tileAIBorder: '#296C6B',


// 70s Lounge Theme (Dark)
export const darkTheme = {
  fontFamily: "'Bungee', cursive",
  instructionFont: "'Nunito', sans-serif",
  foundWordsFont: "'Courier New', 'Monaco', 'Menlo', monospace",
  body: '#3D2B1F', // Dark wood brown (fallback)
  // bodyGradient: 'linear-gradient(135deg, #3D2B1F 0%, #574233 20%, #D9822E 40%, #4B8E8D 60%, #B58929 80%, #3D2B1F 100%)',
  bodyGradient: 'linear-gradient(135deg, #3D2B1F 0%, #84592b  20%, #3D2B1F 40%, #642f37 60%, #743014 80%, #3f0013 100%)',
  text: '#EDE7D9', // Creamy off-white
  textShadow: '3px 3px 4px rgba(0,0,0,0.25)', // Keep the original dark shadow for light text
  tileDefault: '#5A5A5A', // Dark slate
  tileDefaultBorder: '#3C3C3C',
  tilePlayer: '#D9822E', // Bold retro orange (main player color)
  tilePlayerBorder: '#B7620E',
  tileAI: '#4B8E8D', // Muted Teal
  tileAIBorder: '#EDE7D9', // Use high-contrast cream for the border
  tileFound: '#B58929', // Antique gold
  tileFoundBorder: '#936707',
  tileText: '#F5EFE6', // Creamy text on colored tiles
  tileOpponent: '#4B8E8D', // Same as AI for consistency
  tileOpponentBorder: '#EDE7D9', // Use high-contrast cream for the border
  tileSelectedBorder: '#a998ef', // Lighter purple for dark mode
  controlsBackground: '#574233',
  controlsBorder: '#EDE7D9',
  controlsText: '#EDE7D9',
  buttonColor: '#4B8E8D', // Dedicated color for buttons in dark theme
  // Opponent colors for multiplayer (2-5 players) - brighter for dark mode
  opponentColors: [
    '#5DADE2', // Light blue
    '#AF7AC5', // Light purple
    '#EC7063', // Coral red
    '#F8C471', // Light amber
  ],
}; 